<template>
  <section class="reports-table-section reports-profitability-table-section">
    <div class="app-table-wrap reports-table-wrap">
      <div
        class="app-table-scroll"
        :class="{ 'reports-table-scroll--sticky-head': stickyHeader }"
      >
        <table class="app-data-table reports-profitability-table">
          <thead :class="{ 'reports-profitability-table-head--sticky': stickyHeader }">
            <tr>
              <th
                v-for="column in resolvedPivotColumns"
                :key="`pivot-head-${column.key}`"
                :class="pivotHeaderClass(column)"
                rowspan="3"
              >
                {{ column.label }}
              </th>
              <th rowspan="3" class="reports-profitability-main-head">{{ tableText.headers.amount }}</th>
              <th colspan="4">{{ tableText.headers.incomeProgress }}</th>
              <th rowspan="3" class="reports-profitability-main-head">{{ tableText.headers.budgets }}</th>
              <th colspan="12">{{ tableText.headers.expensesProgress }}</th>
              <th colspan="4" class="reports-profitability-main-head">{{ tableText.headers.utility }}</th>
            </tr>
            <tr>
              <th rowspan="2">{{ tableText.headers.total }}</th>
              <th rowspan="2">{{ tableText.headers.percent }}</th>
              <th rowspan="2">{{ tableText.headers.paid }}</th>
              <th rowspan="2">{{ tableText.headers.percent }}</th>
              <th colspan="4" class="reports-profitability-group-head reports-profitability-group-head-expenses">{{ tableText.headers.expenses }}</th>
              <th colspan="4" class="reports-profitability-group-head reports-profitability-group-head-fees group-divider-left">{{ tableText.headers.fees }}</th>
              <th colspan="4" class="reports-profitability-group-head reports-profitability-total-cost-head group-divider-left">{{ tableText.headers.totalExpenses }}</th>
              <th rowspan="2" class="reports-profitability-main-head">{{ tableText.headers.total }}</th>
              <th rowspan="2" class="reports-profitability-main-head">{{ tableText.headers.percent }}</th>
              <th rowspan="2" class="reports-profitability-main-head">{{ tableText.headers.real }}</th>
              <th rowspan="2" class="reports-profitability-main-head">{{ tableText.headers.percent }}</th>
            </tr>
            <tr>
              <th>{{ tableText.headers.total }}</th>
              <th>{{ tableText.headers.percent }}</th>
              <th>{{ tableText.headers.paid }}</th>
              <th class="group-divider-right">{{ tableText.headers.percent }}</th>
              <th class="group-divider-left">{{ tableText.headers.total }}</th>
              <th>{{ tableText.headers.percent }}</th>
              <th>{{ tableText.headers.paid }}</th>
              <th class="group-divider-right">{{ tableText.headers.percent }}</th>
              <th class="reports-profitability-total-cost-subhead group-divider-left">{{ tableText.headers.total }}</th>
              <th class="reports-profitability-total-cost-subhead">{{ tableText.headers.percent }}</th>
              <th class="reports-profitability-total-cost-subhead">{{ tableText.headers.paid }}</th>
              <th class="reports-profitability-total-cost-subhead">{{ tableText.headers.percent }}</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="row in sortedMappedRows"
              :key="`row-${row.id}-${row.rowType}`"
              :class="rowClass(row)"
            >
              <template v-for="column in resolvedPivotColumns">
                <td
                  v-if="shouldRenderPivotCell(row, column)"
                  :key="`pivot-row-${row.id}-${column.key}`"
                  :class="pivotCellClass(row, column)"
                  :colspan="pivotCellColspan(row, column)"
                >
                  <router-link
                    v-if="shouldLinkPivot(row, column.key)"
                    :to="pivotLinkTo(row, column.key)"
                    :class="[
                      'reports-table-link',
                      { 'reports-table-link--customer': column.key === 'cliente' },
                    ]"
                  >
                    {{ resolvePivotCellValue(row, column.key) }}
                  </router-link>
                  <span v-else>{{ resolvePivotCellValue(row, column.key) }}</span>
                </td>
              </template>

              <td class="app-text-right reports-profitability-main-cell">{{ formatCurrency(row.businessAmount) }}</td>

              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(row, 'income_total', row.incomeTotal)"
                  type="button"
                  class="reports-profitability-cell-button"
                  @click.stop="emitMetricCellClick(row, 'income_total', tableText.headers.incomeProgress)"
                >
                  {{ formatCurrency(row.incomeTotal) }}
                </button>
                <span v-else>{{ formatCurrency(row.incomeTotal) }}</span>
              </td>
              <td class="app-text-right">{{ formatPercent(row.incomeProgressTotal) }}</td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(row, 'income_paid', row.incomePaid)"
                  type="button"
                  class="reports-profitability-cell-button"
                  @click.stop="emitMetricCellClick(row, 'income_paid', tableText.headers.incomeProgress)"
                >
                  {{ formatCurrency(row.incomePaid) }}
                </button>
                <span v-else>{{ formatCurrency(row.incomePaid) }}</span>
              </td>
              <td class="app-text-right">{{ formatPercent(row.incomeProgressPaid) }}</td>

              <td class="app-text-right reports-profitability-main-cell">{{ formatCurrency(row.budget) }}</td>

              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(row, 'expenses_total', row.expensesTotal)"
                  type="button"
                  class="reports-profitability-cell-button"
                  @click.stop="emitMetricCellClick(row, 'expenses_total', tableText.headers.expenses)"
                >
                  {{ formatCurrency(row.expensesTotal) }}
                </button>
                <span v-else>{{ formatCurrency(row.expensesTotal) }}</span>
              </td>
              <td class="app-text-right" :class="progressClass(row.expenseProgressTotal)">{{ formatPercent(row.expenseProgressTotal) }}</td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(row, 'expenses_paid', row.expensesPaid)"
                  type="button"
                  class="reports-profitability-cell-button"
                  @click.stop="emitMetricCellClick(row, 'expenses_paid', tableText.headers.expenses)"
                >
                  {{ formatCurrency(row.expensesPaid) }}
                </button>
                <span v-else>{{ formatCurrency(row.expensesPaid) }}</span>
              </td>
              <td class="app-text-right group-divider-right" :class="progressClass(row.expenseProgressPaid)">{{ formatPercent(row.expenseProgressPaid) }}</td>

              <td class="app-text-right group-divider-left">
                <button
                  v-if="canOpenMetricDetail(row, 'fees_total', row.feesTotal)"
                  type="button"
                  class="reports-profitability-cell-button"
                  @click.stop="emitMetricCellClick(row, 'fees_total', tableText.headers.fees)"
                >
                  {{ formatCurrency(row.feesTotal) }}
                </button>
                <span v-else>{{ formatCurrency(row.feesTotal) }}</span>
              </td>
              <td class="app-text-right" :class="progressClass(row.feeProgressTotal)">{{ formatPercent(row.feeProgressTotal) }}</td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(row, 'fees_paid', row.feesPaid)"
                  type="button"
                  class="reports-profitability-cell-button"
                  @click.stop="emitMetricCellClick(row, 'fees_paid', tableText.headers.fees)"
                >
                  {{ formatCurrency(row.feesPaid) }}
                </button>
                <span v-else>{{ formatCurrency(row.feesPaid) }}</span>
              </td>
              <td class="app-text-right group-divider-right" :class="progressClass(row.feeProgressPaid)">{{ formatPercent(row.feeProgressPaid) }}</td>

              <td class="app-text-right group-divider-left">
                <button
                  v-if="canOpenMetricDetail(row, 'total_cost_total', row.totalCostTotal)"
                  type="button"
                  class="reports-profitability-cell-button"
                  @click.stop="emitMetricCellClick(row, 'total_cost_total', tableText.headers.totalExpenses)"
                >
                  {{ formatCurrency(row.totalCostTotal) }}
                </button>
                <span v-else>{{ formatCurrency(row.totalCostTotal) }}</span>
              </td>
              <td class="app-text-right" :class="progressClass(row.totalCostProgressTotal)">{{ formatPercent(row.totalCostProgressTotal) }}</td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(row, 'total_cost_paid', row.totalCostPaid)"
                  type="button"
                  class="reports-profitability-cell-button"
                  @click.stop="emitMetricCellClick(row, 'total_cost_paid', tableText.headers.totalExpenses)"
                >
                  {{ formatCurrency(row.totalCostPaid) }}
                </button>
                <span v-else>{{ formatCurrency(row.totalCostPaid) }}</span>
              </td>
              <td class="app-text-right" :class="progressClass(row.totalCostProgressPaid)">{{ formatPercent(row.totalCostProgressPaid) }}</td>

              <td class="app-text-right app-strong reports-profitability-main-cell">{{ formatCurrency(row.utilityTotalAmount) }}</td>
              <td class="app-text-right app-strong reports-profitability-main-cell">{{ formatPercent(row.utilityTotalPercent) }}</td>
              <td class="app-text-right app-strong reports-profitability-main-cell">{{ formatCurrency(row.utilityRealAmount) }}</td>
              <td class="app-text-right app-strong reports-profitability-main-cell">{{ formatPercent(row.utilityRealPercent) }}</td>
            </tr>

            <tr class="reports-profitability-total-row">
              <td :colspan="resolvedPivotColumns.length"><strong>{{ tableText.totalGeneral }}</strong></td>
              <td class="app-text-right reports-profitability-main-cell"><strong>{{ formatCurrency(resolvedSummary.businessAmount) }}</strong></td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(resolvedSummary, 'income_total', resolvedSummary.incomeTotal)"
                  type="button"
                  class="reports-profitability-cell-button app-strong"
                  @click.stop="emitMetricCellClick(resolvedSummary, 'income_total', tableText.headers.incomeProgress)"
                >
                  {{ formatCurrency(resolvedSummary.incomeTotal) }}
                </button>
                <strong v-else>{{ formatCurrency(resolvedSummary.incomeTotal) }}</strong>
              </td>
              <td class="app-text-right"><strong>{{ formatPercent(resolvedSummary.incomeProgressTotal) }}</strong></td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(resolvedSummary, 'income_paid', resolvedSummary.incomePaid)"
                  type="button"
                  class="reports-profitability-cell-button app-strong"
                  @click.stop="emitMetricCellClick(resolvedSummary, 'income_paid', tableText.headers.incomeProgress)"
                >
                  {{ formatCurrency(resolvedSummary.incomePaid) }}
                </button>
                <strong v-else>{{ formatCurrency(resolvedSummary.incomePaid) }}</strong>
              </td>
              <td class="app-text-right"><strong>{{ formatPercent(resolvedSummary.incomeProgressPaid) }}</strong></td>
              <td class="app-text-right reports-profitability-main-cell"><strong>{{ formatCurrency(resolvedSummary.budget) }}</strong></td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(resolvedSummary, 'expenses_total', resolvedSummary.expensesTotal)"
                  type="button"
                  class="reports-profitability-cell-button app-strong"
                  @click.stop="emitMetricCellClick(resolvedSummary, 'expenses_total', tableText.headers.expenses)"
                >
                  {{ formatCurrency(resolvedSummary.expensesTotal) }}
                </button>
                <strong v-else>{{ formatCurrency(resolvedSummary.expensesTotal) }}</strong>
              </td>
              <td class="app-text-right" :class="progressClass(resolvedSummary.expenseProgressTotal)"><strong>{{ formatPercent(resolvedSummary.expenseProgressTotal) }}</strong></td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(resolvedSummary, 'expenses_paid', resolvedSummary.expensesPaid)"
                  type="button"
                  class="reports-profitability-cell-button app-strong"
                  @click.stop="emitMetricCellClick(resolvedSummary, 'expenses_paid', tableText.headers.expenses)"
                >
                  {{ formatCurrency(resolvedSummary.expensesPaid) }}
                </button>
                <strong v-else>{{ formatCurrency(resolvedSummary.expensesPaid) }}</strong>
              </td>
              <td class="app-text-right group-divider-right" :class="progressClass(resolvedSummary.expenseProgressPaid)"><strong>{{ formatPercent(resolvedSummary.expenseProgressPaid) }}</strong></td>
              <td class="app-text-right group-divider-left">
                <button
                  v-if="canOpenMetricDetail(resolvedSummary, 'fees_total', resolvedSummary.feesTotal)"
                  type="button"
                  class="reports-profitability-cell-button app-strong"
                  @click.stop="emitMetricCellClick(resolvedSummary, 'fees_total', tableText.headers.fees)"
                >
                  {{ formatCurrency(resolvedSummary.feesTotal) }}
                </button>
                <strong v-else>{{ formatCurrency(resolvedSummary.feesTotal) }}</strong>
              </td>
              <td class="app-text-right" :class="progressClass(resolvedSummary.feeProgressTotal)"><strong>{{ formatPercent(resolvedSummary.feeProgressTotal) }}</strong></td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(resolvedSummary, 'fees_paid', resolvedSummary.feesPaid)"
                  type="button"
                  class="reports-profitability-cell-button app-strong"
                  @click.stop="emitMetricCellClick(resolvedSummary, 'fees_paid', tableText.headers.fees)"
                >
                  {{ formatCurrency(resolvedSummary.feesPaid) }}
                </button>
                <strong v-else>{{ formatCurrency(resolvedSummary.feesPaid) }}</strong>
              </td>
              <td class="app-text-right group-divider-right" :class="progressClass(resolvedSummary.feeProgressPaid)"><strong>{{ formatPercent(resolvedSummary.feeProgressPaid) }}</strong></td>
              <td class="app-text-right group-divider-left">
                <button
                  v-if="canOpenMetricDetail(resolvedSummary, 'total_cost_total', resolvedSummary.totalCostTotal)"
                  type="button"
                  class="reports-profitability-cell-button app-strong"
                  @click.stop="emitMetricCellClick(resolvedSummary, 'total_cost_total', tableText.headers.totalExpenses)"
                >
                  {{ formatCurrency(resolvedSummary.totalCostTotal) }}
                </button>
                <strong v-else>{{ formatCurrency(resolvedSummary.totalCostTotal) }}</strong>
              </td>
              <td class="app-text-right" :class="progressClass(resolvedSummary.totalCostProgressTotal)"><strong>{{ formatPercent(resolvedSummary.totalCostProgressTotal) }}</strong></td>
              <td class="app-text-right">
                <button
                  v-if="canOpenMetricDetail(resolvedSummary, 'total_cost_paid', resolvedSummary.totalCostPaid)"
                  type="button"
                  class="reports-profitability-cell-button app-strong"
                  @click.stop="emitMetricCellClick(resolvedSummary, 'total_cost_paid', tableText.headers.totalExpenses)"
                >
                  {{ formatCurrency(resolvedSummary.totalCostPaid) }}
                </button>
                <strong v-else>{{ formatCurrency(resolvedSummary.totalCostPaid) }}</strong>
              </td>
              <td class="app-text-right" :class="progressClass(resolvedSummary.totalCostProgressPaid)"><strong>{{ formatPercent(resolvedSummary.totalCostProgressPaid) }}</strong></td>
              <td class="app-text-right reports-profitability-main-cell"><strong>{{ formatCurrency(resolvedSummary.utilityTotalAmount) }}</strong></td>
              <td class="app-text-right reports-profitability-main-cell"><strong>{{ formatPercent(resolvedSummary.utilityTotalPercent) }}</strong></td>
              <td class="app-text-right reports-profitability-main-cell"><strong>{{ formatCurrency(resolvedSummary.utilityRealAmount) }}</strong></td>
              <td class="app-text-right reports-profitability-main-cell"><strong>{{ formatPercent(resolvedSummary.utilityRealPercent) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import { formatPercent as formatPercentValue } from '@/utils/percentFormat';
import { REPORTS_SALES_TEXT, requireReportsSalesTextValue } from '@/composables/reports/sales/useReportsSalesPresentation';
import {
  REPORTS_SORT_DIRECTION_ASC,
  REPORTS_SORT_DIRECTION_NONE,
  normalizeReportsSortDirection,
} from '@/composables/reports/general/toolbar/useReportsFilterToolbarHelpers';

export default {
  name: 'ReportsSalesProfitabilityTable',
  emits: ['metric-cell-click'],
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    summary: {
      type: Object,
      default: null,
    },
    pivotColumns: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    sortDirection: {
      type: String,
      default: REPORTS_SORT_DIRECTION_NONE,
    },
    stickyHeader: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tableText() {
      const tableText = REPORTS_SALES_TEXT.profitability.table;
      requireReportsSalesTextValue(tableText.totalGeneral, 'reports.sales.profitability.table.totalGeneral');
      return tableText;
    },
    resolvedPivot() {
      const pivot = String(this.filters?.pivot || '').trim().toLowerCase();
      const allowed = [
        'negocio',
        'cliente_negocio',
        'responsable_negocio',
        'vendedor_negocio',
        'servicio_negocio',
      ];

      return allowed.includes(pivot) ? pivot : 'negocio';
    },
    resolvedPivotColumns() {
      const allowed = new Set(['cliente', 'responsable', 'vendedor', 'servicio', 'negocio']);
      const normalizedFromPayload = (Array.isArray(this.pivotColumns) ? this.pivotColumns : [])
        .map((column) => {
          const key = String(column?.key || column?.code || '').trim().toLowerCase();
          const label = String(column?.label || column?.name || '').trim();
          if (!allowed.has(key)) {
            return null;
          }

          return {
            key,
            label: label || this.defaultPivotColumnLabel(key),
          };
        })
        .filter(Boolean);

      if (normalizedFromPayload.length > 0) {
        return normalizedFromPayload;
      }

      return this.fallbackPivotColumnsByFilter();
    },
    mappedRows() {
      return (Array.isArray(this.rows) ? this.rows : []).map((row, index) => {
        const rowType = String(row?.rowType || row?.row_type || '').trim().toLowerCase() || 'detail';
        const isGroupTotal = Boolean(row?.isGroupTotal ?? row?.is_group_total ?? rowType === 'group_total');

        return {
          id: Number(row?.id || index + 1),
          rowType,
          isGroupTotal,
          saleId: Number(row?.saleId || row?.sale_id || 0),
          customerId: Number(row?.customerId || row?.customer_id || 0),
          pivotValue: String(row?.pivotValue || row?.pivot_value || '').trim(),
          pivotValues: this.normalizePivotValues(row?.pivotValues || row?.pivot_values || {}),
          saleCode: String(row?.saleCode || row?.sale_code || '').trim(),
          saleName: String(row?.saleName || row?.sale_name || '').trim(),
          customerName: String(row?.customerName || row?.customer_name || '').trim(),
          managerName: String(row?.managerName || row?.manager_name || '').trim(),
          sellerName: String(row?.sellerName || row?.seller_name || '').trim(),
          productName: String(row?.productName || row?.product_name || '').trim(),
          businessAmount: Number(row?.businessAmount || row?.business_amount || 0),
          incomeTotal: Number(row?.incomeTotal || row?.income_total || 0),
          incomePaid: Number(row?.incomePaid || row?.income_paid || 0),
          incomeProgressTotal: Number(row?.incomeProgressTotal || row?.income_progress_total || 0),
          incomeProgressPaid: Number(row?.incomeProgressPaid || row?.income_progress_paid || 0),
          budget: Number(row?.budget || row?.budget_total || 0),
          expensesTotal: Number(row?.expensesTotal || row?.expenses_total || 0),
          expensesPaid: Number(row?.expensesPaid || row?.expenses_paid || 0),
          expenseProgressTotal: Number(row?.expenseProgressTotal || row?.expense_progress_total || 0),
          expenseProgressPaid: Number(row?.expenseProgressPaid || row?.expense_progress_paid || 0),
          feesTotal: Number(row?.feesTotal || row?.fees_total || 0),
          feesPaid: Number(row?.feesPaid || row?.fees_paid || 0),
          feeProgressTotal: Number(row?.feeProgressTotal || row?.fee_progress_total || 0),
          feeProgressPaid: Number(row?.feeProgressPaid || row?.fee_progress_paid || 0),
          totalCostTotal: Number(row?.totalCostTotal || row?.total_cost_total || 0),
          totalCostPaid: Number(row?.totalCostPaid || row?.total_cost_paid || 0),
          totalCostProgressTotal: Number(row?.totalCostProgressTotal || row?.total_cost_progress_total || 0),
          totalCostProgressPaid: Number(row?.totalCostProgressPaid || row?.total_cost_progress_paid || 0),
          utilityTotalAmount: Number(row?.utilityTotalAmount || row?.utility_total_amount || 0),
          utilityTotalPercent: Number(row?.utilityTotalPercent || row?.utility_total_percent || 0),
          utilityRealAmount: Number(row?.utilityRealAmount || row?.utility_real_amount || 0),
          utilityRealPercent: Number(row?.utilityRealPercent || row?.utility_real_percent || 0),
          detailElementIds: this.normalizeDetailElementIds(row?.detailElementIds || row?.detail_element_ids || {}),
        };
      });
    },
    normalizedSortDirection() {
      return normalizeReportsSortDirection(this.sortDirection);
    },
    sortedMappedRows() {
      return this.sortRowsByUtility(this.mappedRows, this.normalizedSortDirection);
    },
    resolvedSummary() {
      if (this.summary && typeof this.summary === 'object') {
        return {
          businessAmount: Number(this.summary.businessAmount || this.summary.business_amount || 0),
          incomeTotal: Number(this.summary.incomeTotal || this.summary.income_total || 0),
          incomePaid: Number(this.summary.incomePaid || this.summary.income_paid || 0),
          incomeProgressTotal: Number(this.summary.incomeProgressTotal || this.summary.income_progress_total || 0),
          incomeProgressPaid: Number(this.summary.incomeProgressPaid || this.summary.income_progress_paid || 0),
          budget: Number(this.summary.budget || this.summary.budget_total || 0),
          expensesTotal: Number(this.summary.expensesTotal || this.summary.expenses_total || 0),
          expensesPaid: Number(this.summary.expensesPaid || this.summary.expenses_paid || 0),
          expenseProgressTotal: Number(this.summary.expenseProgressTotal || this.summary.expense_progress_total || 0),
          expenseProgressPaid: Number(this.summary.expenseProgressPaid || this.summary.expense_progress_paid || 0),
          feesTotal: Number(this.summary.feesTotal || this.summary.fees_total || 0),
          feesPaid: Number(this.summary.feesPaid || this.summary.fees_paid || 0),
          feeProgressTotal: Number(this.summary.feeProgressTotal || this.summary.fee_progress_total || 0),
          feeProgressPaid: Number(this.summary.feeProgressPaid || this.summary.fee_progress_paid || 0),
          totalCostTotal: Number(this.summary.totalCostTotal || this.summary.total_cost_total || 0),
          totalCostPaid: Number(this.summary.totalCostPaid || this.summary.total_cost_paid || 0),
          totalCostProgressTotal: Number(this.summary.totalCostProgressTotal || this.summary.total_cost_progress_total || 0),
          totalCostProgressPaid: Number(this.summary.totalCostProgressPaid || this.summary.total_cost_progress_paid || 0),
          utilityTotalAmount: Number(this.summary.utilityTotalAmount || this.summary.utility_total_amount || 0),
          utilityTotalPercent: Number(this.summary.utilityTotalPercent || this.summary.utility_total_percent || 0),
          utilityRealAmount: Number(this.summary.utilityRealAmount || this.summary.utility_real_amount || 0),
          utilityRealPercent: Number(this.summary.utilityRealPercent || this.summary.utility_real_percent || 0),
          detailElementIds: this.normalizeDetailElementIds(this.summary.detailElementIds || this.summary.detail_element_ids || {}),
        };
      }

      return this.mappedRows.reduce((acc, row) => {
        if (row.isGroupTotal) {
          return acc;
        }

        acc.businessAmount += row.businessAmount;
        acc.incomeTotal += row.incomeTotal;
        acc.incomePaid += row.incomePaid;
        acc.budget += row.budget;
        acc.expensesTotal += row.expensesTotal;
        acc.expensesPaid += row.expensesPaid;
        acc.feesTotal += row.feesTotal;
        acc.feesPaid += row.feesPaid;
        acc.totalCostTotal += row.totalCostTotal;
        acc.totalCostPaid += row.totalCostPaid;
        acc.utilityTotalAmount += row.utilityTotalAmount;
        acc.utilityRealAmount += row.utilityRealAmount;
        acc.detailElementIds = this.mergeDetailElementIds(acc.detailElementIds, row.detailElementIds);
        return acc;
      }, {
        businessAmount: 0,
        incomeTotal: 0,
        incomePaid: 0,
        incomeProgressTotal: 0,
        incomeProgressPaid: 0,
        budget: 0,
        expensesTotal: 0,
        expensesPaid: 0,
        expenseProgressTotal: 0,
        expenseProgressPaid: 0,
        feesTotal: 0,
        feesPaid: 0,
        feeProgressTotal: 0,
        feeProgressPaid: 0,
        totalCostTotal: 0,
        totalCostPaid: 0,
        totalCostProgressTotal: 0,
        totalCostProgressPaid: 0,
        utilityTotalAmount: 0,
        utilityTotalPercent: 0,
        utilityRealAmount: 0,
        utilityRealPercent: 0,
        detailElementIds: this.normalizeDetailElementIds({}),
      });
    },
  },
  methods: {
    sortPrimaryMetric(row) {
      return Number(row?.utilityTotalAmount || 0);
    },
    sortSecondaryMetric(row) {
      return Number(row?.businessAmount || 0);
    },
    compareRowsByUtility(left, right, direction = REPORTS_SORT_DIRECTION_NONE) {
      const normalizedDirection = normalizeReportsSortDirection(direction);
      const multiplier = normalizedDirection === REPORTS_SORT_DIRECTION_ASC ? 1 : -1;
      const leftPrimary = this.sortPrimaryMetric(left);
      const rightPrimary = this.sortPrimaryMetric(right);

      if (leftPrimary !== rightPrimary) {
        return (leftPrimary - rightPrimary) * multiplier;
      }

      const leftSecondary = this.sortSecondaryMetric(left);
      const rightSecondary = this.sortSecondaryMetric(right);
      if (leftSecondary !== rightSecondary) {
        return (leftSecondary - rightSecondary) * multiplier;
      }

      return Number(left?.id || 0) - Number(right?.id || 0);
    },
    sortRowsByUtility(rows, direction = REPORTS_SORT_DIRECTION_NONE) {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const normalizedDirection = normalizeReportsSortDirection(direction);

      if (normalizedDirection === REPORTS_SORT_DIRECTION_NONE || sourceRows.length <= 1) {
        return sourceRows;
      }

      const hasGroupTotals = sourceRows.some((row) => Boolean(row?.isGroupTotal));
      if (!hasGroupTotals) {
        return [...sourceRows].sort((left, right) => {
          return this.compareRowsByUtility(left, right, normalizedDirection);
        });
      }

      const groups = [];
      let pendingDetailRows = [];

      sourceRows.forEach((row, index) => {
        if (Boolean(row?.isGroupTotal)) {
          const sortedDetailRows = [...pendingDetailRows].sort((left, right) => {
            return this.compareRowsByUtility(left, right, normalizedDirection);
          });

          groups.push({
            stableIndex: index,
            groupTotalRow: row,
            detailRows: sortedDetailRows,
          });

          pendingDetailRows = [];
          return;
        }

        pendingDetailRows.push(row);
      });

      if (pendingDetailRows.length > 0) {
        pendingDetailRows.forEach((row, index) => {
          groups.push({
            stableIndex: sourceRows.length + index,
            groupTotalRow: null,
            detailRows: [row],
          });
        });
      }

      groups.sort((leftGroup, rightGroup) => {
        const leftReference = leftGroup.groupTotalRow || leftGroup.detailRows[0] || null;
        const rightReference = rightGroup.groupTotalRow || rightGroup.detailRows[0] || null;
        const compareResult = this.compareRowsByUtility(leftReference, rightReference, normalizedDirection);

        if (compareResult !== 0) {
          return compareResult;
        }

        return Number(leftGroup.stableIndex || 0) - Number(rightGroup.stableIndex || 0);
      });

      const flattenedRows = [];
      groups.forEach((group) => {
        flattenedRows.push(...group.detailRows);
        if (group.groupTotalRow) {
          flattenedRows.push(group.groupTotalRow);
        }
      });

      return flattenedRows;
    },
    normalizeDetailElementIds(raw) {
      const source = raw && typeof raw === 'object' ? raw : {};
      const normalizeArray = (value) => {
        return (Array.isArray(value) ? value : [])
          .map((item) => Number(item || 0))
          .filter((item) => Number.isInteger(item) && item > 0)
          .filter((item, index, arr) => arr.indexOf(item) === index);
      };

      return {
        income_total: normalizeArray(source.income_total),
        income_paid: normalizeArray(source.income_paid),
        expenses_total: normalizeArray(source.expenses_total),
        expenses_paid: normalizeArray(source.expenses_paid),
        fees_total: normalizeArray(source.fees_total),
        fees_paid: normalizeArray(source.fees_paid),
        total_cost_total: normalizeArray(source.total_cost_total),
        total_cost_paid: normalizeArray(source.total_cost_paid),
      };
    },
    mergeDetailElementIds(base, next) {
      const left = this.normalizeDetailElementIds(base);
      const right = this.normalizeDetailElementIds(next);
      const merge = (first, second) => {
        return [...first, ...second].filter((item, index, arr) => arr.indexOf(item) === index);
      };

      return {
        income_total: merge(left.income_total, right.income_total),
        income_paid: merge(left.income_paid, right.income_paid),
        expenses_total: merge(left.expenses_total, right.expenses_total),
        expenses_paid: merge(left.expenses_paid, right.expenses_paid),
        fees_total: merge(left.fees_total, right.fees_total),
        fees_paid: merge(left.fees_paid, right.fees_paid),
        total_cost_total: merge(left.total_cost_total, right.total_cost_total),
        total_cost_paid: merge(left.total_cost_paid, right.total_cost_paid),
      };
    },
    metricDetailSource(metricCode) {
      return String(metricCode || '').startsWith('income_') ? 'income' : 'expense';
    },
    metricDetailIds(row, metricCode) {
      const detail = this.normalizeDetailElementIds(row?.detailElementIds || row?.detail_element_ids || {});
      return Array.isArray(detail[metricCode]) ? detail[metricCode] : [];
    },
    canOpenMetricDetail(row, metricCode, amountValue) {
      const amount = Number(amountValue || 0);
      if (!Number.isFinite(amount) || Math.abs(amount) <= 0) {
        return false;
      }

      return this.metricDetailIds(row, metricCode).length > 0;
    },
    emitMetricCellClick(row, metricCode, metricLabel) {
      const elementIds = this.metricDetailIds(row, metricCode);
      if (elementIds.length === 0) {
        return;
      }

      const primaryPivotKey = String(this.resolvedPivotColumns[0]?.key || '').trim();
      const secondaryPivotKey = String(this.resolvedPivotColumns[1]?.key || '').trim();
      const primaryPivotName = primaryPivotKey !== ''
        ? String(this.resolvePivotCellValue(row, primaryPivotKey) || '').trim()
        : '';
      const secondaryPivotName = secondaryPivotKey !== ''
        ? String(this.resolvePivotCellValue(row, secondaryPivotKey) || '').trim()
        : '';

      this.$emit('metric-cell-click', {
        element_ids: elementIds,
        detail_source: this.metricDetailSource(metricCode),
        detail_metric: String(metricCode || ''),
        primary_pivot_name: primaryPivotName,
        secondary_pivot_name: secondaryPivotName,
        column_label: String(metricLabel || '').trim(),
        metric_label: String(metricLabel || '').trim(),
      });
    },
    isGroupTotalRow(row) {
      return Boolean(row?.isGroupTotal);
    },
    rowClass(row) {
      return {
        'reports-profitability-group-total-row': this.isGroupTotalRow(row),
      };
    },
    isPrimaryPivotColumn(column) {
      const firstKey = String(this.resolvedPivotColumns[0]?.key || '');
      return String(column?.key || '') === firstKey;
    },
    shouldRenderPivotCell(row, column) {
      if (!this.isGroupTotalRow(row)) {
        return true;
      }

      if (this.resolvedPivotColumns.length <= 1) {
        return true;
      }

      return this.isPrimaryPivotColumn(column);
    },
    pivotCellColspan(row, column) {
      if (!this.isGroupTotalRow(row)) {
        return 1;
      }

      if (this.resolvedPivotColumns.length <= 1) {
        return 1;
      }

      return this.isPrimaryPivotColumn(column)
        ? this.resolvedPivotColumns.length
        : 1;
    },
    pivotHeaderClass(column) {
      return {
        'reports-profitability-col--negocio': column?.key === 'negocio',
      };
    },
    pivotCellClass(row, column) {
      return {
        'reports-profitability-col--negocio': column?.key === 'negocio',
        'reports-profitability-col--group-label': this.isGroupTotalRow(row) && column?.key !== 'negocio',
      };
    },
    shouldLinkPivot(row, key) {
      if (this.isGroupTotalRow(row)) {
        return false;
      }

      if (key === 'negocio') {
        return Number(row?.saleId || 0) > 0;
      }

      if (key === 'cliente') {
        return Number(row?.customerId || 0) > 0;
      }

      return false;
    },
    pivotLinkTo(row, key) {
      if (key === 'cliente') {
        return {
          name: 'customerDetail',
          params: { customerId: Number(row?.customerId || 0) },
        };
      }

      return {
        name: 'saleDetail',
        params: { saleId: Number(row?.saleId || 0) },
      };
    },
    normalizePivotValues(rawValues = {}) {
      if (!rawValues || typeof rawValues !== 'object') {
        return {};
      }

      return Object.entries(rawValues).reduce((acc, [key, value]) => {
        const normalizedKey = String(key || '').trim().toLowerCase();
        if (normalizedKey === '') {
          return acc;
        }

        acc[normalizedKey] = String(value || '').trim();
        return acc;
      }, {});
    },
    defaultPivotColumnLabel(key) {
      const labels = {
        cliente: this.tableText.pivots.cliente,
        responsable: this.tableText.pivots.responsable,
        vendedor: this.tableText.pivots.vendedor,
        servicio: this.tableText.pivots.servicio,
        negocio: this.tableText.pivots.negocio,
        defaultColumn: this.tableText.pivots.defaultColumn,
      };

      return requireReportsSalesTextValue(labels[key] ?? labels.defaultColumn, `reports.sales.profitability.table.pivots.${key}`);
    },
    fallbackPivotColumnsByFilter() {
      const maps = {
        negocio: [{ key: 'negocio', label: this.defaultPivotColumnLabel('negocio') }],
        cliente_negocio: [
          { key: 'cliente', label: this.defaultPivotColumnLabel('cliente') },
          { key: 'negocio', label: this.defaultPivotColumnLabel('negocio') },
        ],
        responsable_negocio: [
          { key: 'responsable', label: this.defaultPivotColumnLabel('responsable') },
          { key: 'negocio', label: this.defaultPivotColumnLabel('negocio') },
        ],
        vendedor_negocio: [
          { key: 'vendedor', label: this.defaultPivotColumnLabel('vendedor') },
          { key: 'negocio', label: this.defaultPivotColumnLabel('negocio') },
        ],
        servicio_negocio: [
          { key: 'servicio', label: this.defaultPivotColumnLabel('servicio') },
          { key: 'negocio', label: this.defaultPivotColumnLabel('negocio') },
        ],
      };

      return maps[this.resolvedPivot] || maps.negocio;
    },
    resolveSaleDisplay(row) {
      const code = String(row?.saleCode || '').trim();
      const name = String(row?.saleName || '').trim();
      if (code !== '' && name !== '') {
        return `${code} - ${name}`;
      }
      if (code !== '') {
        return code;
      }
      if (name !== '') {
        return name;
      }

      return String(row?.pivotValue || '').trim()
        || requireReportsSalesTextValue(this.tableText.pivots.unknownBusiness, 'reports.sales.profitability.table.pivots.unknownBusiness');
    },
    resolvePivotCellValue(row, key) {
      const directValue = String(row?.pivotValues?.[key] || '').trim();
      if (directValue !== '') {
        return directValue;
      }

      if (this.isGroupTotalRow(row)) {
        return '';
      }

      if (key === 'negocio') {
        return this.resolveSaleDisplay(row);
      }

      const fallbackByKey = {
        cliente: String(row?.customerName || '').trim(),
        responsable: String(row?.managerName || '').trim(),
        vendedor: String(row?.sellerName || '').trim(),
        servicio: String(row?.productName || '').trim(),
      };

      return fallbackByKey[key]
        || requireReportsSalesTextValue(this.tableText.pivots.emptyCell, 'reports.sales.profitability.table.pivots.emptyCell');
    },
    formatCurrency(value) {
      return formatCurrency(Number(value || 0), { currency: 'MXN' });
    },
    formatPercent(value) {
      return formatPercentValue(value);
    },
    progressClass(value) {
      const numeric = Number(value || 0);
      return {
        'reports-profitability-progress-alarm': numeric >= 80,
      };
    },
  },
};
</script>
